import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiEdit2, FiEye, FiTrash2, FiGrid, FiList, FiFilter, 
  FiDownload, FiUpload, FiMoreVertical, FiChevronDown,
  FiSearch, FiX, FiImage, FiChevronLeft, FiChevronRight
} from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';
import { Menu, Transition } from '@headlessui/react';
import { useDebounce } from 'use-debounce';
import api from '../../../api/api';
import { toast } from 'react-hot-toast';
import { DEFAULT_PRODUCT_IMAGE } from '../../../utils/constants';

const ProductsList = () => {
  const navigate = useNavigate();
  // State Management
  const [products, setProducts] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    brand: 'all',
    status: 'all',
    priceRange: [0, 100000],
    stockStatus: 'all'
  });
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [sorting, setSorting] = useState({ field: 'createdAt', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // Debounced search
  const [debouncedSearch] = useDebounce(filters.search, 500);

  // Memoized filtered and sorted products
  const filteredProducts = useMemo(() => {
    return products
      .filter(product => {
        const matchesSearch = (
          (product.name?.toLowerCase() || '').includes(debouncedSearch.toLowerCase()) ||
          (product.sku?.toLowerCase() || '').includes(debouncedSearch.toLowerCase())
        );
        const matchesCategory = filters.category === 'all' || product.category?.id === filters.category;
        const matchesBrand = filters.brand === 'all' || product.brand?.id === filters.brand;
        const matchesStatus = filters.status === 'all' || product.status === filters.status;
        const matchesStock = filters.stockStatus === 'all' || 
                           (filters.stockStatus === 'inStock' && (product.totalStock || 0) > 0) ||
                           (filters.stockStatus === 'lowStock' && (product.totalStock || 0) <= 5) ||
                           (filters.stockStatus === 'outOfStock' && (product.totalStock || 0) === 0);
        
        return matchesSearch && matchesCategory && matchesBrand && matchesStatus && matchesStock;
      })
      .sort((a, b) => {
        const direction = sorting.direction === 'asc' ? 1 : -1;
        const aValue = a[sorting.field] ?? '';
        const bValue = b[sorting.field] ?? '';
        return direction * (aValue > bValue ? 1 : -1);
      });
  }, [products, debouncedSearch, filters, sorting]);

  // Pagination
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredProducts, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  // Handlers
  const handleVariantSelect = (productId, variantId) => {
    setSelectedVariants(prev => ({
      ...prev,
      [productId]: variantId
    }));
  };

  const handleBulkAction = async (action) => {
    switch (action) {
      case 'delete':
        // Implement bulk delete
        break;
      case 'export':
        // Implement export
        break;
      case 'activate':
      case 'deactivate':
        // Implement status change
        break;
    }
  };

  const handleEdit = async (productId) => {
    try {
      navigate(`/admin/products/edit/${productId}`);
    } catch (error) {
      console.error('Error navigating to edit page:', error);
      toast.error('Failed to open edit page');
    }
  };

  const handleDelete = (productId) => {
    setProductToDelete(productId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await api.deleteProduct(productToDelete);
      
      // Clear image cache for the deleted product
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_PRODUCT_IMAGES',
          productId: productToDelete
        });
      }
      
      // Update UI
      setProducts(prevProducts => prevProducts.filter(p => p.id !== productToDelete));
      toast.success('Product deleted successfully');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    } finally {
      setShowDeleteModal(false);
      setProductToDelete(null);
    }
  };

  const ProductCard = ({ product }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [imageError, setImageError] = useState(false);

    // Get all images from all variants with cache busting
    const allImages = useMemo(() => {
      const images = [];
      if (product.variants) {
        product.variants.forEach(variant => {
          if (variant.images?.length) {
            images.push(...variant.images.map(img => {
              // Add cache busting parameter
              const imgUrl = img.images;
              return imgUrl + (imgUrl.includes('?') ? '&' : '?') + `v=${Date.now()}`;
            }));
          }
        });
      }
      return images.length ? images : [DEFAULT_PRODUCT_IMAGE];
    }, [product.variants]);

    const handleImageError = (e) => {
      setImageError(true);
      // Clear src to prevent continuous error triggering
      e.target.src = DEFAULT_PRODUCT_IMAGE;
    };

    return (
      <motion.div
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
      >
        <div className="relative group">
          <div className="aspect-w-4 aspect-h-3 bg-gray-100 relative">
            {!imageError ? (
              <img
                src={allImages[currentImageIndex]}
                alt={product.name}
                className="object-cover w-full h-full"
                onError={handleImageError}
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                <FiImage className="w-12 h-12 text-gray-400" />
              </div>
            )}
          </div>
        </div>
        <div className="p-4">
          <h3 className="text-lg font-medium text-gray-900">{product.name}</h3>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-lg font-semibold text-gray-900">₹{product.sale_price || product.price}</span>
            {product.sale_price && (
              <span className="text-sm text-gray-500 line-through">₹{product.price}</span>
            )}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            <span>Category: {product.category?.name}</span>
            <span className="mx-2">•</span>
            <span>Brand: {product.brand?.name}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {product.tags?.map(tag => (
              <span key={tag.id} className="px-2 py-1 text-xs bg-gray-100 rounded-full">
                {tag.name}
              </span>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between">
            <span className={`px-2 py-1 text-xs rounded-full ${
              product.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {product.is_active ? 'Active' : 'Inactive'}
            </span>
            <div className="flex items-center gap-2">
              <Link to={`/admin/products/edit/${product.id}`}>
                <FiEdit2 className="w-5 h-5 text-gray-500 hover:text-gray-700" />
              </Link>
              <button onClick={() => handleDelete(product.id)}>
                <FiTrash2 className="w-5 h-5 text-gray-500 hover:text-red-500" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await api.getProducts();
        
        if (!response || !Array.isArray(response)) {
          throw new Error('Invalid response format');
        }

        // Log the first product to check its structure
        if (response.length > 0) {
          console.log('Sample product data:', response[0]);
        }

        setProducts(response);
      } catch (error) {
        console.error('Error fetching products:', error);
        setError('Failed to fetch products');
        toast.error('Failed to load products');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button 
            onClick={fetchProducts}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Products Management | Admin Dashboard</title>
      </Helmet>

      {/* Header */}
      <div className="bg-white border-b">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Products</h1>
            <div className="flex items-center">
              <Link
                to="/admin/products/add"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Add New Product
              </Link>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-4">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No products found</p>
          </div>
        ) : (
          <div className={`grid gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
              : 'grid-cols-1'
          }`}>
            {paginatedProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredProducts.length)} of {filteredProducts.length} products
          </div>
          <div className="flex gap-2">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded ${
                  currentPage === i + 1
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filter Drawer */}
      <Transition show={isFilterDrawerOpen}>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40">
          <div className="absolute right-0 top-0 h-full w-80 bg-white shadow-xl">
            {/* Add filter controls */}
          </div>
        </div>
      </Transition>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this product? This action cannot be undone.</p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsList;




































